// client/src/pages/admin/ManageTemplateParts.js
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Button from '../../components/ui/Button.js';
import Input, { Select } from '../../components/ui/Input.js';
import Modal from '../../components/ui/Modal.js';

// ─────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────
// Targets a part can belong to. 'shared' parts appear in both views.
const TARGETS = [
  { value: 'htmlcontent',  label: 'Final HTML (recipient view)' },
  { value: 'preview_html', label: 'Preview HTML (sender compose view)' },
  { value: 'shared',       label: 'Shared (both views)' },
];

// Display labels for the part_kind dropdown — purely a label for the
// admin UI, doesn't affect rendering.
const PART_KINDS = [
  { value: 'html_fragment', label: 'HTML Fragment' },
  { value: 'style_block',   label: 'Style Block (CSS)' },
  { value: 'script_block',  label: 'Script Block (JS)' },
];

// Color-coded styling per target for the section headers / badges.
const TARGET_STYLES = {
  htmlcontent:  { color: 'text-blue-300',   bg: 'bg-blue-500/15',   border: 'border-blue-500/40',   label: 'Final HTML' },
  preview_html: { color: 'text-pink-300',   bg: 'bg-pink-500/15',   border: 'border-pink-500/40',   label: 'Preview HTML' },
  shared:       { color: 'text-purple-300', bg: 'bg-purple-500/15', border: 'border-purple-500/40', label: 'Shared' },
};

// Threshold above which we color the line count yellow (gentle warning
// that the part is getting too long to comfortably edit). Just a UI cue,
// no enforcement.
const LINE_COUNT_WARN = 1000;
const LINE_COUNT_ALERT = 1500;

// Friendly label for each target value — used in the export dividers.
const TARGET_LABEL_FULL = {
  htmlcontent:  'Final HTML (recipient view)',
  preview_html: 'Preview HTML (sender compose view)',
  shared:       'Shared (both views)',
};


// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
export default function ManageTemplateParts() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // Toast for export feedback ("Copied! ...").
  const [toast, setToast] = useState(null);  // { message: string } or null

  // Edit/Create modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);  // null = create mode
  const [form, setForm] = useState({
    target: 'htmlcontent',
    part_kind: 'html_fragment',
    name: '',
    content: '',
    display_order: 0,
    isactive: true,
  });

  // Convert-to-parts modal state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);


  // ─── Load template metadata + parts ──────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the template metadata (name, current htmlcontent / preview_html
      // length) so we can show context. We pull from the admin templates
      // endpoint which returns all templates; find ours by id.
      const templatesData = await adminService.getTemplates();
      const found = (templatesData.templates || []).find(
        (t) => String(t.templateid) === String(templateId)
      );
      if (!found) {
        setError('Template not found.');
        setLoading(false);
        return;
      }
      setTemplate(found);

      // Fetch parts
      const partsData = await adminService.getTemplateParts(templateId);
      setParts(partsData.parts || []);
    } catch (err) {
      console.error('Load parts error:', err);
      setError('Failed to load template parts.');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);


  // ─── Open edit modal (create or edit) ────────────────────────────
  const openCreateModal = (defaultTarget = 'htmlcontent') => {
    setEditingPart(null);
    setForm({
      target: defaultTarget,
      part_kind: 'html_fragment',
      name: '',
      content: `<!-- PART: new_part — Describe this part here -->\n`,
      display_order: 0,  // ignored on create — server places at end
      isactive: true,
    });
    setShowEditModal(true);
  };

  const openEditModal = (part) => {
    setEditingPart(part);
    setForm({
      target: part.target,
      part_kind: part.part_kind || 'html_fragment',
      name: part.name || '',
      content: part.content || '',
      display_order: part.display_order || 0,
      isactive: part.isactive !== false,
    });
    setShowEditModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert('Name is required.');
      return;
    }
    try {
      if (editingPart) {
        await adminService.updateTemplatePart(templateId, editingPart.partid, form);
      } else {
        // Don't send display_order on create — let the server place it at the end
        const { display_order, ...createPayload } = form;
        await adminService.createTemplatePart(templateId, createPayload);
      }
      setShowEditModal(false);
      setEditingPart(null);
      await load();
    } catch (err) {
      console.error('Save part error:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to save part.';
      alert(msg);
    }
  };

  const handleDelete = async (part) => {
    if (!window.confirm(`Delete part "${part.name}"? This cannot be undone.`)) return;
    try {
      await adminService.deleteTemplatePart(templateId, part.partid);
      await load();
    } catch (err) {
      console.error('Delete part error:', err);
      alert('Failed to delete part.');
    }
  };


  // ─── Reorder (up/down arrows) ─────────────────────────────────────
  // Reorders within the entire parts list — preserves the cross-target
  // display_order semantics (a 'shared' part can move between two
  // 'htmlcontent' parts or two 'preview_html' parts).
  const movePart = async (partid, direction) => {
    const idx = parts.findIndex((p) => p.partid === partid);
    if (idx === -1) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= parts.length) return;

    const newOrder = [...parts];
    [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];

    // Optimistic update
    setParts(newOrder);

    try {
      await adminService.reorderTemplateParts(
        templateId,
        newOrder.map((p) => p.partid)
      );
    } catch (err) {
      console.error('Reorder error:', err);
      alert('Failed to reorder. Reloading...');
      await load();
    }
  };


  // ─── Convert to parts mode ────────────────────────────────────────
  // Called from the conversion banner. Three options:
  //   'blank'       — start with an empty parts list
  //   'import_html' — copy the existing htmlcontent into one part
  //   'import_both' — copy both htmlcontent and preview_html into parts
  const handleConvert = async (mode) => {
    if (!template) return;
    setConverting(true);
    try {
      if (mode === 'blank') {
        // Just create one empty placeholder part so the template enters parts mode.
        // The admin can then build out from there.
        await adminService.createTemplatePart(templateId, {
          target: 'htmlcontent',
          part_kind: 'html_fragment',
          name: 'placeholder',
          content: '<!-- PART: placeholder — Replace this with your first part -->\n',
          isactive: true,
        });
      } else if (mode === 'import_html') {
        if (!template.htmlcontent) {
          alert('This template has no Final HTML content to import.');
          setConverting(false);
          return;
        }
        await adminService.createTemplatePart(templateId, {
          target: 'htmlcontent',
          part_kind: 'html_fragment',
          name: 'full_card',
          content: template.htmlcontent,
          isactive: true,
        });
      } else if (mode === 'import_both') {
        if (template.htmlcontent) {
          await adminService.createTemplatePart(templateId, {
            target: 'htmlcontent',
            part_kind: 'html_fragment',
            name: 'full_card',
            content: template.htmlcontent,
            isactive: true,
          });
        }
        if (template.preview_html) {
          await adminService.createTemplatePart(templateId, {
            target: 'preview_html',
            part_kind: 'html_fragment',
            name: 'full_preview',
            content: template.preview_html,
            isactive: true,
          });
        }
      }
      setShowConvertModal(false);
      await load();
    } catch (err) {
      console.error('Convert error:', err);
      alert('Failed to convert template. Please try again.');
    } finally {
      setConverting(false);
    }
  };


  // ─── EXPORT / COPY HELPERS ────────────────────────────────────────
  // These mirror the server-side assembly logic in
  // server/services/templateAssembly.js so the admin can preview exactly
  // what the recipient/sender will see — no round-trip to the server.

  /**
   * Build the assembled HTML string for a given target ('htmlcontent' or
   * 'preview_html'). Includes parts whose target matches OR is 'shared',
   * sorted by display_order, joined with newlines. Inactive parts are
   * excluded (matching server behavior).
   */
  const assembleForTarget = (target) => {
    return parts
      .filter((p) => p.isactive !== false)
      .filter((p) => p.target === target || p.target === 'shared')
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map((p) => p.content || '')
      .join('\n');
  };

  /**
   * Build the annotated "all parts" view: every part regardless of target,
   * in display_order, with a header explaining the format and a divider
   * between each part showing its identity. This is the version intended
   * for handing to an LLM or human reviewer.
   *
   * Inactive parts are INCLUDED (with a clear "(INACTIVE)" tag) so the
   * reviewer sees the full picture.
   */
  const buildAnnotatedExport = () => {
    const sorted = [...parts].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const totalLines = sorted.reduce((sum, p) => sum + (p.line_count || 0), 0);
    const today = new Date().toISOString().slice(0, 10);

    const divider = '='.repeat(80);
    const sectionDivider = '-'.repeat(80);

    let out = '';
    out += divider + '\n';
    out += 'TEMPLATE PARTS — CONCATENATED VIEW\n';
    out += divider + '\n\n';
    out += 'This file is a CONCATENATED VIEW of multiple parts stored in the eCard\n';
    out += 'template_html_parts database table. It is intended for review only —\n';
    out += 'this exact text is NEVER stored as a single record anywhere.\n\n';
    out += 'If you need to modify any code below:\n';
    out += '  - Identify which PART contains it (each section starts with a divider)\n';
    out += '  - Edit that PART INDIVIDUALLY in the admin Template Parts page\n';
    out += '  - Do NOT paste this combined file back as a single part\n\n';
    out += `Template:     ${template?.name || '(unknown)'}\n`;
    out += `Parts count:  ${sorted.length}\n`;
    out += `Total lines:  ${totalLines.toLocaleString()}\n`;
    out += `Generated:    ${today}\n\n`;
    out += 'Each part below is preceded by a divider showing its name, target, and\n';
    out += 'display order. The system assembles them in display_order at render time\n';
    out += 'by concatenating their content fields with newlines between them.\n\n';
    out += 'TARGETS:\n';
    out += '  Final HTML    = part appears only in the recipient view\n';
    out += '  Preview HTML  = part appears only in the sender compose view\n';
    out += '  Shared        = part appears in BOTH views\n\n';
    out += divider + '\n\n';

    for (const part of sorted) {
      const targetLabel = TARGET_LABEL_FULL[part.target] || part.target;
      const inactiveTag = part.isactive === false ? '  (INACTIVE - not included in live assembly)' : '';
      out += '\n' + divider + '\n';
      out += `PART #${part.display_order}: ${part.name}${inactiveTag}\n`;
      out += `TARGET:  ${targetLabel}\n`;
      out += `LINES:   ${(part.line_count || 0).toLocaleString()}\n`;
      out += divider + '\n\n';
      out += (part.content || '') + '\n';
    }

    out += '\n' + sectionDivider + '\n';
    out += `END OF CONCATENATED VIEW (${sorted.length} parts, ${totalLines.toLocaleString()} lines)\n`;
    out += sectionDivider + '\n';

    return out;
  };

  /**
   * Copy text to the clipboard. Falls back to a hidden textarea + execCommand
   * if the modern Clipboard API isn't available (e.g., non-secure contexts).
   * Returns true on success, false on failure.
   */
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      // Fallback for HTTP / older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      return false;
    }
  };

  /** Brief on-screen confirmation after a copy action. */
  const showToast = (message) => {
    setToast({ message });
    setTimeout(() => setToast(null), 2400);
  };

  /** Handler factory: produce a button click that copies the chosen export. */
  const handleExport = async (kind) => {
    let text = '';
    let label = '';
    let partCount = 0;

    if (kind === 'final') {
      text = assembleForTarget('htmlcontent');
      label = 'Final HTML';
      partCount = parts.filter(p => p.isactive !== false && (p.target === 'htmlcontent' || p.target === 'shared')).length;
    } else if (kind === 'preview') {
      text = assembleForTarget('preview_html');
      label = 'Preview HTML';
      partCount = parts.filter(p => p.isactive !== false && (p.target === 'preview_html' || p.target === 'shared')).length;
    } else if (kind === 'all') {
      text = buildAnnotatedExport();
      label = 'All Parts (annotated)';
      partCount = parts.length;
    }

    if (!text || !text.trim()) {
      showToast(`Nothing to copy — no ${label.toLowerCase()} content found.`);
      return;
    }

    const lineCount = text.split('\n').length;
    const ok = await copyToClipboard(text);
    if (ok) {
      showToast(`✓ Copied ${label} — ${partCount} part${partCount === 1 ? '' : 's'} • ${lineCount.toLocaleString()} lines`);
    } else {
      showToast('Copy failed — your browser may have blocked clipboard access.');
    }
  };


  // ─── Render: loading / error states ───────────────────────────────
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center text-royal-200">
        Loading template parts...
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <p className="text-red-400 text-center">{error}</p>
        <div className="text-center mt-6">
          <Link to="/admin/templates" className="text-gold underline hover:text-gold-light">
            ← Back to Templates
          </Link>
        </div>
      </div>
    );
  }


  // ─── Render: main page ────────────────────────────────────────────
  const inPartsMode = parts.length > 0;
  const finalLineCount  = (template?.htmlcontent  || '').split('\n').length;
  const previewLineCount = (template?.preview_html || '').split('\n').length;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Back link */}
      <div className="mb-5">
        <Link
          to="/admin/templates"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Templates
        </Link>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl text-gold">Template Parts</h1>
        <p className="text-royal-200 mt-1">
          {template?.name}
          {inPartsMode && (
            <span className="ml-3 px-2 py-0.5 bg-cyan-500/20 text-cyan-300 text-xs rounded-full align-middle">
              Multi-Part
            </span>
          )}
        </p>
      </div>

      {/* ───────── HELP / EXPLANATION PANEL ─────────
          Collapsible. Closed by default once an admin has used the page
          a few times, but the toggle is always visible at the top so a
          first-time reader can expand it for a full explanation. */}
      <div className="mb-4 bg-royal-800/40 border border-royal-600 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowHelp(!showHelp)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-royal-800/60 transition-colors"
        >
          <span className="text-royal-200 text-sm">
            🧩 <span className="text-gold font-medium">How parts mode works</span>
            <span className="text-royal-200 ml-2">— click to {showHelp ? 'hide' : 'expand'}</span>
          </span>
          <span className="text-gold text-sm">{showHelp ? '▲' : '▼'}</span>
        </button>

        {showHelp && (
          <div className="px-5 py-4 border-t border-royal-700 text-royal-200 text-sm space-y-3 leading-relaxed">

            <p>
              <span className="text-gold font-semibold">What it is.</span>{' '}
              Normally an eCard template stores its full HTML in two columns: <code className="text-gold">htmlcontent</code> (what recipients see) and <code className="text-gold">preview_html</code> (what senders see while composing). For most cards this works fine. But when a card grows past 1,500–2,000 lines, those single fields get hard for a human or AI to edit safely — small changes in one place accidentally break something far away. <span className="text-gold">Parts mode</span> is the alternative: instead of one long string per column, you store the same content as a <em>list of smaller chunks</em> (parts). When someone opens the card, the server pulls all the parts out of the database, glues them back together in order, and serves them as if they were one big file. To the recipient, nothing has changed. To you as an editor, each piece is small enough to work on without losing track.
            </p>

            <p>
              <span className="text-gold font-semibold">How the assembly works (the entire mechanism).</span>{' '}
              Each part is a row in the <code className="text-gold">template_html_parts</code> table with three things that matter: a <span className="text-gold">target</span> (which view it belongs to — <em>Final HTML</em>, <em>Preview HTML</em>, or <em>Shared</em>), the <span className="text-gold">content</span> (the raw HTML/CSS/JS for that piece), and a <span className="text-gold">display_order</span> number. When the system needs to render the card, it runs a query like <em>"give me all parts for this template where the target matches what I need, sorted by display_order"</em>, then concatenates the <code className="text-gold">content</code> fields with newlines between them. That joined string is the assembled HTML. There is no other logic. No magic, no markers, no special syntax — just a sorted list of strings glued end-to-end.
            </p>

            <p>
              <span className="text-gold font-semibold">What this means in practice.</span>{' '}
              Because assembly is just concatenation, the system doesn't care <em>where</em> you split a file — it cares that the joined result is valid HTML. If part #1 ends mid-CSS-block, part #2 needs to continue that CSS block correctly, or close it. If part #1 opens a <code className="text-gold">{'<script>'}</code> tag, the closing <code className="text-gold">{'</script>'}</code> needs to live in some later part. As long as the final concatenated output would parse as a valid HTML document, you can split anywhere. You could split a card into 3 parts, 8 parts, 20 parts — whatever keeps each piece a manageable size. The split points are about <em>your editing convenience</em>, not about clean architectural boundaries.
            </p>

            <p>
              <span className="text-gold font-semibold">Targets — and why "Shared" exists.</span>{' '}
              Recipient view (<em>Final HTML</em>) and sender compose view (<em>Preview HTML</em>) often share huge amounts of code — the rendering engine, animations, styles. To avoid storing the same code twice, mark a part as <em>Shared</em> and it gets included in <strong>both</strong> assemblies. So a typical big card might have, say, 3 Final HTML parts + 3 Preview HTML parts + 2 Shared parts. The Shared parts appear in their <code className="text-gold">display_order</code> position within whichever sequence is being assembled. There is no separate "shared section" — Shared parts mix into the main flow.
            </p>

            <p>
              <span className="text-gold font-semibold">Order matters.</span>{' '}
              The <code className="text-gold">display_order</code> column controls where each part lands in the final glued-together file. Use the up/down arrows on each part card to nudge them around, or type a specific number into the Display Order field when editing. When you reorder, the system renumbers everything to clean integers (1, 2, 3, ...) automatically. New parts you create are placed at the end by default.
            </p>

            <p>
              <span className="text-gold font-semibold">The PART comment is a convention, not a rule.</span>{' '}
              Each part's content can begin with an HTML comment like <code className="text-gold">{'<!-- PART: name — description -->'}</code>. The system doesn't read it — HTML comments are inert at runtime, so they have no effect on the rendered card. The comment exists purely so that when you (or an LLM) open a single part in isolation, the very first line tells you what the part is. Without it, you'd be staring at a chunk of CSS or JavaScript with no context. You can place the comment at the top, at the bottom, or omit it entirely; the assembled card will still work. The admin parts list parses this comment to show the friendly description under each part name — so omitting it just means you'll see no description in the list. Strongly recommended at the top of each part as the first line.
            </p>

            <p>
              <span className="text-gold font-semibold">Single-file mode is still the default.</span>{' '}
              A template only enters parts mode if you've created at least one part for it. Cards without parts continue using the old <code className="text-gold">htmlcontent</code> and <code className="text-gold">preview_html</code> columns exactly as before. There is no flag to flip and no setting to configure — the existence of parts <em>is</em> the signal. If you ever delete every part from a template, it automatically reverts to single-file mode and the original column content (which we never touched) takes over again.
            </p>

            <p>
              <span className="text-gold font-semibold">Soft disable a part.</span>{' '}
              Each part has an <em>Active</em> checkbox. Unchecking it removes the part from the assembled output without deleting the row, useful for quick rollback or A/B testing. If you uncheck every part in a template, the system has no active parts to assemble and falls back to the legacy single-file content.
            </p>

            <p>
              <span className="text-gold font-semibold">Export buttons (parts mode only).</span>{' '}
              When a template is in parts mode, three Export buttons appear above the parts list. Use them to copy assembled views to your clipboard for offline review, browser testing, or handing to an LLM for analysis. <em>Copy Final HTML</em> and <em>Copy Preview HTML</em> produce the exact text the server would assemble for each view (no dividers — pure HTML). <em>Copy All Parts (annotated)</em> produces a single text block containing every part with clear dividers and a header explaining the format — best for review. None of these are stored anywhere; they are clipboard exports only.
            </p>

            <p className="text-royal-200">
              <span className="text-gold font-semibold">Bottom line:</span>{' '}
              parts mode is a way to chop a long file into smaller manageable pieces that the server pastes back together at render time. It's about <em>length</em>, not architecture. Cards under ~1,500 lines should generally stay single-file. Cards above that benefit from being split into 3–8 chunks of 500–1,000 lines each.
            </p>
          </div>
        )}
      </div>

      {/* Conversion banner — shown only when no parts exist */}
      {!inPartsMode && (
        <div className="mb-6 p-5 rounded-lg bg-royal-800/40 border border-royal-600">
          <p className="text-white font-semibold mb-2">
            This template currently uses single-file mode.
          </p>
          <p className="text-royal-200 text-sm mb-4">
            Final HTML: <span className="text-gold">{finalLineCount.toLocaleString()} lines</span>{' '}
            &middot; Preview HTML: <span className="text-gold">{previewLineCount.toLocaleString()} lines</span>
          </p>
          <Button onClick={() => setShowConvertModal(true)}>
            Convert to Multi-Part
          </Button>
        </div>
      )}

      {/* ───────── EXPORT BUTTONS ─────────
          Three clipboard-export buttons. Only shown when in parts mode.
          - Copy Final HTML: the assembled recipient view (htmlcontent + shared)
          - Copy Preview HTML: the assembled sender view (preview_html + shared)
          - Copy All Parts: every part with header explanation + dividers,
            for review or feeding to an LLM. */}
      {inPartsMode && (
        <div className="mb-4 p-4 bg-royal-800/30 border border-royal-700 rounded-lg">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-royal-200 text-sm">
              📋 <span className="text-gold font-medium">Export</span>
              <span className="text-royal-200 ml-2">— copy assembled views to your clipboard</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleExport('final')}
              className="px-4 py-2 text-sm rounded-lg bg-blue-500/15 text-blue-300 border border-blue-500/40 hover:bg-blue-500/25 transition-colors"
              title="Copy the assembled recipient view (htmlcontent + shared parts) — no dividers, plain HTML"
            >
              Copy Final HTML
            </button>
            <button
              onClick={() => handleExport('preview')}
              className="px-4 py-2 text-sm rounded-lg bg-pink-500/15 text-pink-300 border border-pink-500/40 hover:bg-pink-500/25 transition-colors"
              title="Copy the assembled sender compose view (preview_html + shared parts) — no dividers, plain HTML"
            >
              Copy Preview HTML
            </button>
            <button
              onClick={() => handleExport('all')}
              className="px-4 py-2 text-sm rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/40 hover:bg-purple-500/25 transition-colors"
              title="Copy every part with a header explanation and clear dividers between parts — best for review or feeding to an LLM"
            >
              Copy All Parts (annotated)
            </button>
          </div>
        </div>
      )}

      {/* Parts list — shown when in parts mode */}
      {inPartsMode && (
        <>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-royal-200 text-sm">
              {parts.length} part{parts.length === 1 ? '' : 's'} &middot; assembled in display order
            </p>
            <div className="flex gap-2">
              <Button onClick={() => openCreateModal('htmlcontent')}>+ Add Part</Button>
            </div>
          </div>

          <div className="space-y-3">
            {parts.map((part, idx) => {
              const style = TARGET_STYLES[part.target] || TARGET_STYLES.htmlcontent;
              const lineWarn  = part.line_count >= LINE_COUNT_ALERT ? 'text-red-400'
                              : part.line_count >= LINE_COUNT_WARN  ? 'text-yellow-400'
                              : 'text-royal-200';
              return (
                <div
                  key={part.partid}
                  className={`bg-royal-800/40 border rounded-lg p-4 ${style.border} ${!part.isactive ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: content info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-royal-200 text-sm font-mono">
                          #{part.display_order}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${style.bg} ${style.color}`}>
                          {style.label}
                        </span>
                        <span className="text-white font-semibold truncate">{part.name}</span>
                        {!part.isactive && (
                          <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      {part.description && (
                        <p className="text-royal-200 text-sm italic">{part.description}</p>
                      )}
                      <p className={`text-xs mt-1 ${lineWarn}`}>
                        {part.line_count?.toLocaleString() || 0} lines
                        {part.part_kind && part.part_kind !== 'html_fragment' && (
                          <span className="text-royal-200"> &middot; {part.part_kind.replace('_', ' ')}</span>
                        )}
                      </p>
                    </div>

                    {/* Right: action buttons */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => movePart(part.partid, -1)}
                        disabled={idx === 0}
                        className="w-8 h-8 rounded border border-royal-600 text-royal-200 hover:bg-royal-700/50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        title="Move up"
                      >↑</button>
                      <button
                        onClick={() => movePart(part.partid, 1)}
                        disabled={idx === parts.length - 1}
                        className="w-8 h-8 rounded border border-royal-600 text-royal-200 hover:bg-royal-700/50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        title="Move down"
                      >↓</button>
                      <button
                        onClick={() => openEditModal(part)}
                        className="ml-2 text-gold text-sm hover:text-gold-light transition-colors"
                      >Edit</button>
                      <button
                        onClick={() => handleDelete(part)}
                        className="ml-2 text-red-400 text-sm hover:text-red-300 transition-colors"
                      >Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ───────── EDIT / CREATE MODAL ───────── */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingPart(null); }}
        title={editingPart ? `Edit Part: ${editingPart.name}` : 'New Part'}
        maxWidth="max-w-4xl"
      >
        <form onSubmit={handleSave} className="max-h-[80vh] overflow-y-auto pr-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. styles_and_shell"
              required
            />
            <Select
              label="Target"
              value={form.target}
              onChange={(e) => setForm({ ...form, target: e.target.value })}
              required
            >
              {TARGETS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Kind (label only)"
              value={form.part_kind}
              onChange={(e) => setForm({ ...form, part_kind: e.target.value })}
            >
              {PART_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </Select>
            {editingPart && (
              <Input
                label="Display Order"
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
              />
            )}
          </div>

          <label className="flex items-center gap-2 text-royal-200 text-sm">
            <input
              type="checkbox"
              checked={form.isactive}
              onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
            />
            Active (uncheck to disable this part without deleting it)
          </label>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">
              Content
              <span className="text-royal-200 ml-2 text-xs">
                ({(form.content || '').split('\n').length.toLocaleString()} lines)
              </span>
            </label>
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={28}
              required
              placeholder='<!-- PART: name — description -->&#10;...HTML/CSS/JS content here...'
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors font-mono resize-y"
            />
            <p className="text-royal-200 text-xs mt-1">
              Tip: start with <code className="text-gold">{'<!-- PART: ' + (form.name || 'name') + ' — Description -->'}</code> so the file is self-describing when opened in isolation.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setShowEditModal(false); setEditingPart(null); }}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editingPart ? 'Save Changes' : 'Create Part'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ───────── CONVERT MODAL ───────── */}
      <Modal
        isOpen={showConvertModal}
        onClose={() => !converting && setShowConvertModal(false)}
        title="Convert to Multi-Part"
        maxWidth="max-w-lg"
      >
        <div className="space-y-3">
          <p className="text-royal-200 text-sm mb-3">
            Choose how to start. You can always edit, split, and reorder parts after conversion.
          </p>

          <button
            disabled={converting}
            onClick={() => handleConvert('import_both')}
            className="w-full text-left p-4 rounded-lg border border-royal-600 hover:border-gold/50 hover:bg-royal-800/40 transition-colors disabled:opacity-50"
          >
            <p className="text-white font-semibold mb-1">Import both as separate parts</p>
            <p className="text-royal-200 text-sm">
              Copy <span className="text-gold">htmlcontent</span> and <span className="text-gold">preview_html</span> into one part each. Best when both fields are populated.
            </p>
          </button>

          <button
            disabled={converting}
            onClick={() => handleConvert('import_html')}
            className="w-full text-left p-4 rounded-lg border border-royal-600 hover:border-gold/50 hover:bg-royal-800/40 transition-colors disabled:opacity-50"
          >
            <p className="text-white font-semibold mb-1">Import Final HTML as one part</p>
            <p className="text-royal-200 text-sm">
              Copy just <span className="text-gold">htmlcontent</span> into a single part. Split it later by adding more parts.
            </p>
          </button>

          <button
            disabled={converting}
            onClick={() => handleConvert('blank')}
            className="w-full text-left p-4 rounded-lg border border-royal-600 hover:border-gold/50 hover:bg-royal-800/40 transition-colors disabled:opacity-50"
          >
            <p className="text-white font-semibold mb-1">Start blank</p>
            <p className="text-royal-200 text-sm">
              Create an empty placeholder part. Best when you plan to paste in fresh content piece by piece.
            </p>
          </button>

          <div className="flex justify-end pt-3">
            <Button
              variant="outline"
              onClick={() => setShowConvertModal(false)}
              disabled={converting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* ───────── TOAST (export feedback) ───────── */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-royal-900 border border-gold/40 text-gold shadow-lg shadow-gold/20 text-sm">
          {toast.message}
        </div>
      )}
    </div>
  );
}